<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent class="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{{ isEditing ? t('reviews.edit_review') : t('reviews.write_a_review') }}</DialogTitle>
      </DialogHeader>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Title Input -->
        <div class="space-y-2">
          <Label for="review-title">{{ t('reviews.review_title') }} *</Label>
          <Input
            id="review-title"
            v-model="form.title"
            :placeholder="t('reviews.review_title_placeholder')"
            :class="{ 'border-red-500': validationErrors.title }"
          />
          <p v-if="validationErrors.title" class="text-sm text-red-500">
            {{ validationErrors.title }}
          </p>
        </div>
        <!-- Rating -->
        <div class="space-y-2">
          <Label>{{ t('reviews.review_rating') }} *</Label>
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-1">
              <button
                v-for="star in 5"
                :key="star"
                type="button"
                @click="setRating(star)"
                @mouseover="hoverRating = star"
                @mouseleave="hoverRating = 0"
                class="p-1 transition-colors hover:scale-110 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 rounded"
              >
                <svg
                  :class="[
                    'w-6 h-6 transition-all duration-200',
                    (hoverRating || form.rating) >= star ? 'text-yellow-400' : 'text-gray-300',
                    (hoverRating || form.rating) >= star ? 'fill-yellow-400' : 'fill-transparent',
                  ]"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="1"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </button>
            </div>
            <span class="text-sm text-gray-600">{{ form.rating }} / 5</span>
          </div>
          <p v-if="validationErrors.rating" class="text-sm text-red-500">
            {{ validationErrors.rating }}
          </p>
        </div>

        <!-- Review Content -->
        <div class="space-y-2">
          <Label for="review-content">{{ t('reviews.review_content') }} *</Label>
          <Textarea
            id="review-content"
            v-model="form.content"
            :placeholder="t('reviews.review_content_placeholder')"
            rows="5"
            :class="{ 'border-red-500': validationErrors.content }"
          />
          <p v-if="validationErrors.content" class="text-sm text-red-500">
            {{ validationErrors.content }}
          </p>
        </div>

        <!-- Additional Criteria -->
        <!-- <div v-if="criteria && criteria.length > 0" class="space-y-4">
          <h4 class="font-medium">{{ t('reviews.additional_criteria') }}</h4>
          <div v-for="criterion in criteria" :key="criterion.id_criteria" class="space-y-2">
            <div class="flex justify-between items-center">
              <Label>{{ criterion.name }}</Label>
              <span class="text-sm text-gray-600">{{ criteriaRatings[criterion.id_criteria] || 0 }} / 5</span>
            </div>
            <div class="flex items-center gap-1">
              <button
                v-for="star in 5"
                :key="`${criterion.id_criteria}-${star}`"
                type="button"
                @click="setCriteriaRating(criterion.id_criteria, star)"
                @mouseover="criteriaHover[criterion.id_criteria] = star"
                @mouseleave="criteriaHover[criterion.id_criteria] = 0"
                class="p-1 transition-colors hover:scale-110 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 rounded"
              >
                <svg
                  :class="[
                    'w-5 h-5 transition-all duration-200',
                    (criteriaHover[criterion.id_criteria] || criteriaRatings[criterion.id_criteria] || 0) >= star
                      ? 'text-yellow-400'
                      : 'text-gray-300',
                    (criteriaHover[criterion.id_criteria] || criteriaRatings[criterion.id_criteria] || 0) >= star
                      ? 'fill-yellow-400'
                      : 'fill-transparent',
                  ]"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="1"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </button>
            </div>
          </div>        
        </div> -->

        <!-- Error Message -->
        <Alert v-if="formError" variant="destructive">
          <AlertCircle class="h-4 w-4" />
          <AlertTitle>{{ t('reviews.error') }}</AlertTitle>
          <AlertDescription>{{ formError }}</AlertDescription>
        </Alert>
      </form>

      <DialogFooter class="gap-2">
        <Button type="button" variant="outline" @click="handleCancel">
          {{ t('reviews.cancel') }}
        </Button>
        <Button type="button" :disabled="isSubmitting" @click="handleSubmit">
          <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin" />
          {{ isSubmitting ? t('reviews.submitting') : isEditing ? t('reviews.update_review') : t('reviews.submit_review') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, reactive, watch } from 'vue';
import type { PropType } from 'vue';
import type { ProductReview, ProductReviewCriteria } from '@vue-storefront/prestashop-api';
import { AlertCircle, Loader2 } from 'lucide-vue-next';
import Alert from '~/components/ui/alert/Alert.vue';
import AlertDescription from '~/components/ui/alert/AlertDescription.vue';
import AlertTitle from '~/components/ui/alert/AlertTitle.vue';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';

interface ReviewFormData {
  title: string;
  content: string;
  rating: number;
}

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  review: {
    type: Object as PropType<ProductReview | null>,
    default: null,
  },
  criteria: {
    type: Array as PropType<ProductReviewCriteria[]>,
    default: () => [],
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  formError: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{
  'update:open': [value: boolean];
  submit: [
    data: {
      title: string;
      content: string;
      rating: number;
      criteria: Array<{ id_criteria: number; rating: number }>;
    },
  ];
  cancel: [];
}>();

// Composables
const { t } = useI18n();

// State
const hoverRating = ref(0);
const criteriaHover = reactive<Record<number, number>>({});
const criteriaRatings = reactive<Record<number, number>>({});

// Form state
const form = reactive<ReviewFormData>({
  title: '',
  content: '',
  rating: 0,
});

const validationErrors = reactive<Record<string, string>>({
  title: '',
  content: '',
  rating: '',
});

// Methods
const resetForm = () => {
  form.title = '';
  form.content = '';
  form.rating = 0;
  Object.keys(validationErrors).forEach((key) => {
    validationErrors[key] = '';
  });
  props.criteria.forEach((criterion) => {
    criteriaRatings[criterion.id_criteria] = 0;
  });
};

const setRating = (rating: number) => {
  form.rating = rating;
  if (rating > 0) {
    validationErrors.rating = '';
  }
};

const setCriteriaRating = (criterionId: number, rating: number) => {
  criteriaRatings[criterionId] = rating;
};

// Computed
const isEditing = computed(() => !!props.review);

// Watchers
watch(
  () => props.review,
  (newReview) => {
    if (newReview) {
      form.title = newReview.title || '';
      form.content = newReview.content || '';
      form.rating = newReview.rating || 0;

      // Set criteria ratings if available
      if (newReview.criteria?.length) {
        newReview.criteria.forEach((criterion) => {
          criteriaRatings[criterion.id_criteria] = criterion.rating;
        });
      }
    } else {
      resetForm();
    }
  },
  { immediate: true },
);

// Watch for dialog open state changes to reset form
watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) {
      resetForm();
    }
  },
);

const validateForm = () => {
  let isValid = true;

  // Reset previous errors
  Object.keys(validationErrors).forEach((key) => {
    validationErrors[key] = '';
  });

  if (!form.title.trim()) {
    validationErrors.title = t('reviews.error_title_required');
    isValid = false;
  } else if (form.title.length < 3) {
    validationErrors.title = t('reviews.error_title_too_short');
    isValid = false;
  }

  if (!form.content.trim()) {
    validationErrors.content = t('reviews.error_content_required');
    isValid = false;
  } else if (form.content.length < 10) {
    validationErrors.content = t('reviews.error_content_too_short');
    isValid = false;
  }

  if (form.rating <= 0) {
    validationErrors.rating = t('reviews.error_rating_required');
    isValid = false;
  }

  return isValid;
};

const handleSubmit = () => {
  if (!validateForm()) {
    return;
  }

  const reviewData = {
    title: form.title.trim(),
    content: form.content.trim(),
    rating: form.rating,
    criteria: Object.entries(criteriaRatings)
      .filter(([_, rating]) => rating > 0)
      .map(([id, rating]) => ({
        id_criteria: Number(id),
        rating,
      })),
  };

  emit('submit', reviewData);
};

const handleCancel = () => {
  emit('cancel');
  emit('update:open', false);
};

// Initialize criteria hover state
watch(
  () => props.criteria,
  (newCriteria) => {
    newCriteria.forEach((criterion) => {
      criteriaHover[criterion.id_criteria] = 0;
    });
  },
  { immediate: true },
);
</script>
